import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'

// Minimal surface of the global the CF script installs -- just what this
// component uses. See https://developers.cloudflare.com/turnstile/get-started/client-side-rendering/
interface TurnstileGlobal {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string
      callback: (token: string) => void
      'expired-callback'?: () => void
      'error-callback'?: () => void
    },
  ) => string
  reset: (widgetId: string) => void
  remove: (widgetId: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileGlobal
  }
}

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js'

// Cloudflare's script is safe to load more than once, but there is no
// reason to fetch it twice if two widgets ever mount at once.
let scriptPromise: Promise<void> | null = null
function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve()
  scriptPromise ??= new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = SCRIPT_SRC
    script.async = true
    script.defer = true
    script.addEventListener('load', () => resolve())
    script.addEventListener('error', () => reject(new Error('Failed to load Turnstile')))
    document.head.appendChild(script)
  })
  return scriptPromise
}

export interface TurnstileHandle {
  // A token is single-use: after a failed submit that reached the server,
  // the caller must reset before the widget can produce a fresh one.
  reset: () => void
}

interface TurnstileProps {
  siteKey: string
  onToken: (token: string) => void
  onExpire?: () => void
  onError?: () => void
}

export const Turnstile = forwardRef<TurnstileHandle, TurnstileProps>(function Turnstile(
  { siteKey, onToken, onExpire, onError },
  ref,
) {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (widgetIdRef.current) window.turnstile?.reset(widgetIdRef.current)
    },
  }))

  useEffect(() => {
    let cancelled = false

    loadScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: onToken,
          'expired-callback': onExpire,
          'error-callback': onError,
        })
      })
      .catch(() => onError?.())

    return () => {
      cancelled = true
      if (widgetIdRef.current) window.turnstile?.remove(widgetIdRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- callbacks are stable from the caller's useCallback
  }, [siteKey])

  return <div ref={containerRef} />
})
